<template>
  <v-dialog v-model="show" width="100vw" scrollable persistent>
    <v-card
      style="max-height: 90vh; display: flex; flex-direction: column"
      class="elevation-0"
    >
      <v-card-title class="headline primary white--text" style="flex: 0 0 auto">
        <v-icon left color="white">mdi-flask-outline</v-icon>
        Registro de Bioensayo
        <v-row>
          <v-col class="text-end">
            <v-btn text dark rounded @click="show = false">
              <v-icon large> mdi-close </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text style="overflow-y: auto; flex: 1 1 auto">
        <v-hover v-slot="{ hover }" v-if="bioassaySelected">
          <v-expansion-panels class="my-5">
            <v-expansion-panel
              class="smooth-expand"
              :class="{ 'elevation-6': hover }"
              style="border-left: 4px solid #04c105; transition: all 0.3s ease"
            >
              <v-expansion-panel-header
                class="py-4 px-4 gradient-header"
                :class="{ secondary: !hover, primary: hover }"
                expand-icon="mdi-chevron-down"
                disable-icon-rotate
              >
                <v-row align="center" no-gutters>
                  <v-avatar color="white" size="36" class="mr-3">
                    <v-icon color="primary" size="24">mdi-chart-bubble</v-icon>
                  </v-avatar>
                  <v-col>
                    <h3 class="text-body-1 white--text font-weight-bold mb-1">
                      Información inicial
                    </h3>
                    <span class="caption white--text text--lighten-2">
                      {{
                        bioassaySelected.molecularMarkers.length
                          ? `${bioassaySelected.molecularMarkers.length} marcadores detectados`
                          : "Sin marcadores"
                      }}
                    </span>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-chip small dark color="accent" class="mr-2">
                    <v-icon left small>mdi-flask</v-icon>
                    {{ bioassaySelected.products.length }} producto(s)
                  </v-chip>
                </v-row>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="animated-content">
                <v-container fluid class="pt-4 px-4">
                  <v-row class="align-center mb-4">
                    <v-col cols="12" md="3" class="py-0">
                      <div class="d-flex align-center">
                        <v-icon color="secondary" class="mr-2">mdi-dna</v-icon>
                        <span
                          class="subtitle-1 font-weight-bold secondary--text"
                          >Biomarcadores</span
                        >
                        <v-chip
                          small
                          color="secondary"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ bioassaySelected.molecularMarkers.length }}
                        </v-chip>
                      </div>
                    </v-col>
                    <v-col cols="12" md="9" class="py-0">
                      <div class="chip-container">
                        <v-tooltip
                          top
                          v-for="(
                            marker, index
                          ) in bioassaySelected.molecularMarkers"
                          :key="index + marker"
                        >
                          <template v-slot:activator="{ on }">
                            <v-chip
                              v-on="on"
                              :color="index % 2 === 0 ? 'primary' : 'secondary'"
                              class="ma-1 animated-chip"
                              label
                              small
                              :outlined="index % 3 === 0"
                              :text-color="
                                index % 3 === 0 ? 'secondary' : 'white'
                              "
                            >
                              <v-icon left small>{{
                                marker.markerType == "MO"
                                  ? "mdi-bacteria"
                                  : "mdi-leaf"
                              }}</v-icon>
                              {{ marker.molecularMarkerID }} -
                              {{
                                marker.markerType == "MO"
                                  ? marker.microorganism_meta.name
                                  : marker.soilFunction.es
                              }}
                            </v-chip>
                          </template>
                          <span>{{
                            marker.markerType == "MO"
                              ? "Taxonomico"
                              : "Función de suelo"
                          }}</span>
                          <template v-if="marker.markerType != 'MO'">
                            <br />
                            <span> Función: {{ marker.soilFunction.es }} </span>
                            <br />
                            <span> Proceso: {{ marker.process.es }} </span>
                          </template>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>

                  <v-divider
                    class="my-3"
                    style="border-color: rgba(4, 193, 5, 0.2)"
                  ></v-divider>

                  <v-row class="align-center">
                    <v-col cols="12" md="3" class="py-0">
                      <div class="d-flex align-center">
                        <v-icon color="warning" class="mr-2"
                          >mdi-flask-outline</v-icon
                        >
                        <span class="subtitle-1 font-weight-bold warning--text"
                          >Productos</span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="9" class="py-0">
                      <div class="chip-container">
                        <v-tooltip
                          top
                          v-for="(product, index) in bioassaySelected.products"
                          :key="index"
                        >
                          <template v-slot:activator="{ on }">
                            <v-chip
                              v-on="on"
                              :color="index % 2 === 0 ? 'warning' : 'info'"
                              dark
                              class="ma-1 product-chip"
                              pill
                              @click="handleProductSelected(product._id)"
                            >
                              <v-avatar left>
                                <v-img
                                  v-if="product.image"
                                  :src="product.image"
                                ></v-img>
                                <v-icon v-else small
                                  >mdi-bottle-tonic-outline</v-icon
                                >
                              </v-avatar>
                              {{ product.name }}
                              <v-icon right small>mdi-arrow-top-right</v-icon>
                            </v-chip>
                          </template>
                          <span>Ver detalles de {{ product.name }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-hover>
        <v-stepper v-model="currentStep" vertical elevation="0">
          <v-stepper-step editable :complete="isDisableField()" step="1">
            Información General
            <small>Datos básicos del bioassay</small>
          </v-stepper-step>

          <v-stepper-content step="1">
            <v-form ref="generalForm" lazy-validation>
              <v-card flat class="mb-4">
                <v-card-text>
                  <v-subheader class="pl-0 primary--text text--darken-2">
                    <v-icon left color="primary">mdi-flask</v-icon>
                    Información del Producto
                  </v-subheader>

                  <v-row class="mt-2">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="bioassay.productQuantity"
                        label="Requerimiento de producto (Cantidad)"
                        outlined
                        clearable
                        prepend-icon="mdi-tag"
                        dense
                        :disabled="isDisableField()"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="menuProductReception"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="bioassay.productReception"
                            label="Recepción de producto"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :rules="[rules.required]"
                            :disabled="isDisableField()"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          locale="es"
                          v-model="bioassay.productReception"
                          @input="menuProductReception = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-subheader class="pl-0 primary--text text--darken-2 mt-4">
                    <v-icon left color="primary">mdi-cog</v-icon>
                    Configuración del Bioensayo
                  </v-subheader>

                  <v-row class="mt-2">
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="bioassay.dosesQuantity"
                        label="Número de dosis"
                        type="number"
                        min="1"
                        max="10"
                        outlined
                        prepend-icon="mdi-numeric"
                        dense
                        :rules="[rules.required]"
                        :disabled="isDisableField()"
                        @change="handleUpdateDosesQuantity"
                      ></v-text-field>
                    </v-col>
                    <template v-if="bioassay.dosesQuantity">
                      <v-col
                        cols="12"
                        md="4"
                        v-for="(dose, index) in bioassay.doseSize"
                        :key="index"
                      >
                        <v-text-field
                          v-model="dose.size"
                          :label="`Cantidad en dosis ${index + 1}`"
                          :suffix="units"
                          outlined
                          prepend-icon="mdi-water"
                          dense
                          :rules="[rules.required]"
                          :disabled="isDisableField()"
                        ></v-text-field>
                      </v-col>
                    </template>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="bioassay.soilWeight"
                        label="Requerimiento suelo (peso)"
                        suffix="kg"
                        outlined
                        prepend-icon="mdi-scale"
                        dense
                        :rules="[rules.required]"
                        :disabled="isDisableField()"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="bioassay.crops"
                        :items="crops_items"
                        label="Requerimiento de suelo cultivo"
                        outlined
                        item-text="text"
                        prepend-icon="mdi-sprout"
                        color="primary"
                        dense
                        multiple
                        chips
                        :rules="[rules.required]"
                        :disabled="isDisableField()"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-subheader class="pl-0 primary--text text--darken-2 mt-4">
                    <v-icon left color="primary">mdi-earth</v-icon>
                    Información del Suelo
                  </v-subheader>

                  <v-row
                    class="mt-2"
                    v-for="(soil, index) in soils"
                    :key="index"
                  >
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="soil.soilControl"
                        label="Suelo de control"
                        :disabled="isDisableField()"
                        dense
                        clearable
                        outlined
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="soil.crop"
                        :items="crops_items"
                        label="Cultivo del suelo"
                        outlined
                        item-text="text"
                        prepend-icon="mdi-sprout"
                        dense
                        :disabled="isDisableField()"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-menu
                        v-model="soil.menuSoilReception"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="soil.soilReception"
                            label="Recepción de suelo"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :disabled="isDisableField() || shouldDisableSoilReception(index)"
                            clearable
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          locale="es"
                          v-model="soil.soilReception"
                          @input="soil.menuSoilReception = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col
                      cols="12"
                      class="text-right"
                      v-if="index === soils.length - 1"
                    >
                      <v-btn
                        color="primary"
                        small
                        @click="addSoil"
                        :disabled="isDisableField()"
                      >
                        <v-icon left>mdi-plus</v-icon>
                        Agregar otro suelo
                      </v-btn>
                      <v-btn
                        color="error"
                        small
                        @click="removeSoil(index)"
                        v-if="soils.length > 1"
                        :disabled="isDisableField()"
                        class="ml-2"
                      >
                        <v-icon left>mdi-minus</v-icon>
                        Eliminar
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-subheader class="pl-0 primary--text text--darken-2 mt-4">
                    <v-icon left color="primary">mdi-microscope</v-icon>
                    Configuración Experimental
                  </v-subheader>

                  <v-row class="mt-2">
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="menuStartDate"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="bioassay.bioassayStartDate"
                            label="Fecha de inicio del bioassay"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :rules="[rules.required]"
                            :disabled="isDisableField()"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          locale="es"
                          v-model="bioassay.bioassayStartDate"
                          @input="menuStartDate = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="bioassay.replicatesPerDose"
                        label="Número de réplicas por dosis"
                        type="number"
                        min="1"
                        outlined
                        prepend-icon="mdi-repeat"
                        dense
                        :rules="[rules.required]"
                        :disabled="isDisableField()"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-subheader class="pl-0 primary--text text--darken-2 mt-4">
                    <v-icon left color="primary">mdi-note</v-icon>
                    Observations
                  </v-subheader>
                  <v-row class="mt-2">
                    <v-col cols="12">
                      <v-textarea
                        v-model="bioassay.observations"
                        label="Observations generales"
                        outlined
                        rows="3"
                        prepend-icon="mdi-pencil"
                        :disabled="isDisableField()"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-row class="d-flex justify-end">
                <v-col cols="auto">
                  <v-btn
                    color="gray"
                    dark
                    large
                    @click="updateBioassayRequest('save_data')"
                    :disabled="isDisableField()"
                    :loading="isSaving"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Guardar
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="warning"
                    dark
                    large
                    @click="showFinishSetupModal = true"
                    :disabled="isDisableField()"
                    :loading="isFinishSetup"
                  >
                    <v-icon left>mdi-alert</v-icon>
                    Finalizar SetUp
                  </v-btn>
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-4"></div>
            </v-form>
          </v-stepper-content>
          <template v-if="bioassay.status != 'NEW'">
            <template v-for="(time, index) in times">
              <v-stepper-step
                editable
                :key="`step-${index + 2}`"
                :complete="time.isCompleted"
                :step="index + 2"
              >
                Tiempo {{ time.days }} días
                <small>Registro del tiempo {{ index + 1 }}</small>
              </v-stepper-step>

              <v-stepper-content
                :key="`content-${index + 2}`"
                :step="index + 2"
              >
                <v-form :ref="`timesForm_${index}`" lazy-validation>
                  <v-card flat class="mb-4">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-menu
                            v-model="menuSendDate[index]"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="time.sendDate"
                                :label="`Fecha envío tiempo ${index + 1}`"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                outlined
                                dense
                                clearable
                                :rules="[rules.required]"
                                :disabled="time.isCompleted"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              locale="es"
                              v-model="time.sendDate"
                              @input="menuSendDate[index] = false"
                            ></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="12" md="6" v-if="isBiological">
                          <v-text-field
                            v-model="time.ufc"
                            :label="`UFCs tiempo ${index + 1}`"
                            suffix="UFC/ml"
                            outlined
                            dense
                            :disabled="time.isCompleted"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-textarea
                            v-model="time.observations"
                            :label="`Observations tiempo ${index + 1}`"
                            outlined
                            rows="2"
                            :disabled="time.isCompleted"
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-form>

                <v-row>
                  <v-col cols="auto">
                    <v-btn
                      color="warning"
                      :disabled="time.isCompleted"
                      :loading="time.isLoading"
                      @click="
                        (currentIndex = index),
                          (showConfirmationTimeModal = true)
                      "
                    >
                      <v-icon left>mdi-alert</v-icon>
                      Marcar como enviado
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      color="primary"
                      @click="currentStep = index + 3"
                      v-if="index < times.length - 1"
                    >
                      Siguiente tiempo
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      color="primary"
                      @click="currentStep = index + 1"
                      text
                      class="ml-2"
                    >
                      Atrás
                    </v-btn>
                  </v-col>
                  <v-col class="d-flex justify-end">
                    <v-btn
                      color="gray"
                      :dark="!time.isCompleted"
                      large
                      @click="updateBioassayRequest('save_data')"
                      :loading="isSaving"
                      :disabled="time.isCompleted"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Guardar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>
            </template>

            <v-stepper-step
              editable
              :complete="currentStep > times.length + 2"
              :step="times.length + 2"
            >
              Finalización
              <small>Completar el bioassay</small>
            </v-stepper-step>

            <v-stepper-content :step="times.length + 2">
              <v-form ref="finalForm" lazy-validation>
                <v-card flat class="mb-4">
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-menu
                          v-model="menuFinishDate"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="bioassay.endDateBioassay"
                              label="Fecha de finalización proceso de Microbiología en bioensayo"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              :rules="[rules.required]"
                              :disabled="isBioassayProcessFinished()"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            locale="es"
                            v-model="bioassay.endDateBioassay"
                            @input="menuFinishDate = false"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="bioassay.endObservations"
                          label="Observations finales"
                          outlined
                          rows="3"
                          :disabled="isBioassayProcessFinished()"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-form>

              <v-btn
                color="success"
                @click="finishBioassay"
                :disabled="isBioassayProcessFinished()"
                :loading="isFinishMicrobiologicalProcess"
              >
                <v-icon left>mdi-content-save</v-icon>
                Finalizar proceso de Microbiología en Bioensayo
              </v-btn>
              <v-btn
                color="primary"
                text
                @click="currentStep = times.length + 1"
                class="ml-2"
              >
                Atrás
              </v-btn>
            </v-stepper-content>
          </template>
        </v-stepper>
      </v-card-text>
    </v-card>
    <FinishSetupModal
      v-model="showFinishSetupModal"
      title="¿Está seguro de finalizar el setup del bioensayo?"
      @confirmation="updateBioassayRequest('finish_setup')"
    />
    <ConfirmationTimeModal
      v-model="showConfirmationTimeModal"
      title="¿Está seguro de marcar como enviado este tiempo?"
      @confirmation="updateSoilSend(currentIndex)"
    />
    <ProductModal
      v-model="showProductModal"
      v-if="showProductModal"
      @close="handleProductClose"
    />
  </v-dialog>
</template>

<script>
//Vuex
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import FinishSetupModal from "../../utils/GenericConfirmationModal.vue";
import ConfirmationTimeModal from "../../utils/GenericConfirmationModal.vue";
import ProductModal from "../../products/modals/ProductModal.vue";
export default {
  components: {
    FinishSetupModal,
    ConfirmationTimeModal,
    ProductModal,
  },
  data: () => ({
    currentStep: 1,
    validFinal: false,
    menuProductReception: false,
    menuStartDate: false,
    menuFinishDate: false,
    menuSendDate: [],
    menuSoilReception: false,
    crops_items: [],
    rules: {
      required: (value) => !!value || "Campo requerido",
      min1: (value) => value >= 1 || "Mínimo 1",
    },
    bioassay: {
      productQuantity: null,
      productReception: null,
      dosesQuantity: 3,
      doseSize: [],
      soilWeight: null,
      soilReception: null,
      bioassayStartDate: null,
      replicatesPerDose: 3,
      observations: null,
      endDateBioassay: null,
      status: null,
      endObservations: null,
      crops: [],
    },
    soils: [
      {
        soilControl: null,
        crop: null,
        soilReception: null,
        menuSoilReception: null,
      },
    ],
    times: [
      {
        days: 0,
        sendDate: null,
        ufc: null,
        observations: "",
        isCompleted: false,
        isLoading: false,
      },
      {
        days: 10,
        sendDate: null,
        ufc: null,
        observations: "",
        isCompleted: false,
        isLoading: false,
      },
      {
        days: 20,
        sendDate: null,
        ufc: null,
        observations: "",
        isCompleted: false,
        isLoading: false,
      },
    ],
    showFinishSetupModal: false,
    showConfirmationTimeModal: false,
    showProductModal: false,
    currentIndex: null,
    isFinishSetup: false,
    isSaving: false,
    isFinishMicrobiologicalProcess: false,
    units: "",
  }),
  props: {
    value: Boolean,
  },
  computed: {
    ...mapState("reception", ["crops"]),
    ...mapState("bioassays", ["bioassaySelected"]),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  mounted() {
    this.setCropItems();
    this.mapContent();
    this.moveCurrentStep();
  },
  methods: {
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapMutations("bioassays", ["SET_PRODUCT_ID", "SET_BIOASSAY"]),
    ...mapActions("bioassays", [
      "updateBioassay",
      "finishSettingBioassay",
      "updateTimes",
    ]),

    validateStep1() {
      if (this.$refs.generalForm.validate()) {
        this.currentStep = 2;
      }
    },

    async updateBioassayRequest(stage) {
      try {
        let payload = {
          ...this.bioassay,
          soils: this.soils.map((soil) => ({
            soilControl: soil.soilControl,
            crop: soil.crop,
            soilReception: soil.soilReception,
          })),
          times: this.times.map((element) => {
            return {
              sendDate: element.sendDate || null,
              concentration: element.ufc || null,
              observations: element.observations || null,
            };
          }),
        };
        let isValid = true;
        switch (stage) {
          case "save_data":
            this.isSaving = true;
            delete payload.status;
            break;
          case "finish_setup":
            this.isFinishSetup = true;
            payload.status = "SETUP";
            isValid = this.$refs.generalForm.validate();
            break;
        }

        if (isValid) {
          let params = {
            bioassayId: this.bioassaySelected._id,
          };
          let response = await this.updateBioassay({
            body: payload,
            params,
          });
          this.bioassay.status = response.bioassay.status;
          this.$emit("update");
          this.SET_ALERT_TYPE({
            visible: true,
            type: "success",
            text: `Información guardada`,
            timeout: 5000,
          });
        }
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isSaving = false;
        this.isFinishSetup = false;
      }
    },

    async finishBioassay() {
      if (this.$refs.finalForm.validate()) {
        try {
          this.isFinishMicrobiologicalProcess = true;
          let payload = {
            ...this.bioassay,
          };
          delete payload.status;
          let params = {
            bioassayId: this.bioassaySelected._id,
          };
          await this.finishSettingBioassay({
            body: payload,
            params,
          });
          this.bioassay.isCompletedMicrobiologicalBioassay = true;
          this.$emit("update");
          this.SET_ALERT_TYPE({
            visible: true,
            type: "success",
            text: `Bioensayo terminado con exito`,
            timeout: 5000,
          });
        } catch (error) {
          console.log(error);
          this.SET_ALERT_TYPE({
            visible: true,
            type: "error",
            text: `Algo salió mal. Error: ${error.message}`,
            timeout: 5000,
          });
        } finally {
          this.isFinishMicrobiologicalProcess = false;
        }
      }
    },

    async updateSoilSend(index) {
      if (this.$refs[`timesForm_${index}`][0].validate()) {
        try {
          this.times[index].isLoading = true;
          let payload = {
            data: this.times.map((element, innerIndex) => {
              return {
                sendDate: element.sendDate || null,
                concentration: element.ufc || null,
                observations: element.observations || null,
                isCompleted: innerIndex == index || element.isCompleted,
              };
            }),
            index: index,
            generateEmail: true,
          };
          let params = {
            bioassayId: this.bioassaySelected._id,
          };
          await this.updateTimes({
            body: payload,
            params,
          });

          this.times[index].isCompleted = true;
          this.$emit("update");
          this.SET_ALERT_TYPE({
            visible: true,
            type: "success",
            text: `Tiempo ${index} finalizado`,
            timeout: 5000,
          });
        } catch (error) {
          console.log(error);
          this.SET_ALERT_TYPE({
            visible: true,
            type: "error",
            text: `Algo salió mal. Error: ${error.message}`,
            timeout: 5000,
          });
        } finally {
          this.times[index].isLoading = false;
        }
      }
    },

    addSoil() {
      this.soils.push({
        soilControl: null,
        crop: null,
      });
    },

    removeSoil(index) {
      this.soils.splice(index, 1);
    },

    mapContent() {
      if (!this.bioassaySelected) return;

      this.bioassay = {
        productQuantity: this.bioassaySelected.productQuantity,
        productReception: this.bioassaySelected.productReception,
        dosesQuantity: this.bioassaySelected.dosesQuantity,
        doseSize: this.bioassaySelected.doseSize || [],
        soilWeight: this.bioassaySelected.soilWeight,
        soilReception: this.bioassaySelected.soilReception,
        bioassayStartDate: this.bioassaySelected.bioassayStartDate,
        replicatesPerDose: this.bioassaySelected.replicatesPerDose,
        observations: this.bioassaySelected.observations,
        endDateBioassay: this.bioassaySelected.endDateBioassay,
        status: this.bioassaySelected.status,
        endObservations: this.bioassaySelected.endObservations,
        crops: this.bioassaySelected.crops,
        isCompletedMicrobiologicalBioassay:
          this.bioassaySelected.isCompletedMicrobiologicalBioassay || false,
      };

      if (
        this.bioassaySelected.soils &&
        this.bioassaySelected.soils.length > 0
      ) {
        this.soils = this.bioassaySelected.soils.map((soil) => ({
          soilControl: soil.soilControl,
          crop: soil.crop,
          soilReception: soil.soilReception,
          menuSoilReception: null,
        }));
      } else {
        this.soils = [
          {
            soilControl: null,
            crop: null,
            soilReception: null,
            menuSoilReception: null,
          },
        ];
      }
      if (!this.bioassaySelected.times) return;
      for (let index = 0; index < this.times.length; index++) {
        this.times[index].sendDate =
          this.bioassaySelected.times[index]?.sendDate;
        this.times[index].ufc =
          this.bioassaySelected.times[index]?.concentration;
        this.times[index].observations =
          this.bioassaySelected.times[index]?.observations;
        this.times[index].isCompleted =
          this.bioassaySelected.times[index]?.isCompleted || false;
      }

      if (this.$refs.generalForm) {
        this.$refs.generalForm.resetValidation();
      }

      if (
        this.bioassaySelected.products &&
        this.bioassaySelected.products.length > 0
      ) {
        let firstProduct = this.bioassaySelected.products[0];
        if (firstProduct.formulation) {
          if (firstProduct.formulation == "SOLID") {
            this.units = "g/L";
          } else {
            this.units = "ml/L";
          }
        }
      }
    },

    isBiological() {
      return (
        this.bioassaySelected.products.find(
          (element) => element.isBiological
        ) || false
      );
    },

    validateFields(fields) {
      for (const key in fields) {
        if (fields[key] === null || fields[key] === "") {
          return false;
        }
        if (fields[key] === undefined) {
          return false;
        }
      }
      return true;
    },

    validateTimes(times) {
      if (!Array.isArray(times)) {
        return false;
      }

      for (const time of times) {
        if (time.sendDate === null || time.sendDate === "") {
          return false;
        }

        if (this.isBiological() && (time.ufc === null || time.ufc === "")) {
          return false;
        }
      }
      return true;
    },

    resetForm() {
      this.$refs.generalForm.reset();
      this.$refs.finalForm.reset();
      this.$refs.timesForm.forEach((form) => form.reset());
      this.currentStep = 1;
    },

    setCropItems() {
      let cropsArray = Object.entries(this.crops);
      for (let crop of cropsArray) {
        this.crops_items.push({
          text: crop[1],
          value: crop[0],
        });
      }
    },

    isDisableField() {
      return this.bioassay.status != "NEW";
    },
    isBioassayProcessFinished() {
      return this.bioassay.isCompletedMicrobiologicalBioassay;
    },
    moveCurrentStep() {
      if (this.bioassay.status == "NEW") {
        this.currentStep = 1;
      } else if (this.bioassaySelected.times) {
        if (
          !!this.bioassaySelected.times[0] &&
          !this.bioassaySelected.times[0].isCompleted
        ) {
          this.currentStep = 2;
        } else if (
          !!this.bioassaySelected.times[1] &&
          !this.bioassaySelected.times[1].isCompleted
        ) {
          this.currentStep = 3;
        } else if (
          !!this.bioassaySelected.times[2] &&
          !this.bioassaySelected.times[2].isCompleted
        ) {
          this.currentStep = 4;
        } else {
          this.currentStep = 5;
        }
      }
    },
    handleProductSelected(productId) {
      this.SET_PRODUCT_ID(productId);
      this.showProductModal = true;
    },
    handleProductClose() {
      this.showProductModal = false;
      this.SET_PRODUCT_ID(null);
    },
    handleUpdateDosesQuantity() {
      if (this.bioassay.dosesQuantity) {
        let tempArray = [];
        for (
          let index = 0;
          index < parseInt(this.bioassay.dosesQuantity);
          index++
        ) {
          tempArray.push({ size: null });
        }
        this.bioassay.doseSize = tempArray;
      } else {
        this.bioassay.doseSize = [];
      }
    },
shouldDisableSoilReception(currentIndex) {
  const anySoilHasDate = this.soils.some(soil => soil.soilReception);
  return anySoilHasDate && !this.soils[currentIndex].soilReception;
}
  
  },
  watch: {},
};
</script>
<style scoped>
.v-stepper {
  box-shadow: none;
}
.v-card__title {
  padding: 16px 24px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.gradient-header {
  background: linear-gradient(
    45deg,
    var(--v-secondary-base),
    var(--v-primary-base)
  );
  transition: all 0.3s ease;
}

.animated-chip {
  transition: transform 0.2s;
}
.animated-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
}

.product-chip {
  transition: all 0.3s ease;
}
.product-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.animated-content {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.smooth-expand {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
</style>
